import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CommonButton from '../../Components/CommonButton';
import ModuleIcon from '../../Components/ModuleIcon';
import CommonSearchBar from '../../Components/CommonSearchBar';
import CommonDropdown from '../../Components/CommonDropdown';
import CommonTable from '../../Components/CommonTable';
import CommonModal from '../../Components/CommonModal';
import CommonTabs from '../../Components/CommonTabs';
import CommonPagination from '../../Components/CommonPagination';
import ButtonLoader from '../../Components/ButtonLoader';
import { useConfirm } from '../../Components/ConfirmModal';
import CreateTPRMQuestionModal from '../../Dashboard/ThirdpartyExternalExposure/CreateTPRMQuestionModal';
import BulkUploadQuestionsModal from '../../Dashboard/ThirdpartyExternalExposure/BulkUploadQuestionsModal';
import {
    thirdpartyExternalExposureQuestionsService,
    deleteThirdpartyQuestionsService,
    getThirdpartyQuestionsTemplateService,
} from '../../services';
import { MISSING_TEXT, safeArray } from '../tpcrmUtils';
import '../../styles/TpcrmPortal.scss';

const PER_PAGE = 10;

// Table View is flat, so it keeps both Questionnaire and Category as
// columns — there's no heading to carry that information instead. Category
// View nests Questionnaire → Category as headings, so its innermost table
// needs neither.
const TABLE_HEADERS = ['Question', 'Questionnaire', 'Category', 'Response Type', 'Source', 'Action'];
const NESTED_HEADERS = ['Question', 'Response Type', 'Source', 'Action'];

// Assignment/assessment browsing used to also live here as a second tab
// ("Assignments"), scoped to one questionnaire at a time. Removed once the
// dedicated Assessments page (TpcrmAssessmentsView.js, /tpcrm/assessments)
// existed — that page is a strict superset of what the tab did (same
// getTprmAssessmentsService data, plus vendor search, a Status filter, and
// no forced questionnaire pre-selection), so keeping both was pure
// redundancy. This page is Question Bank only now.
function TpcrmQuestionnairesView() {
    const [search, setSearch] = useState('');
    const [questionnaireFilter, setQuestionnaireFilter] = useState('');
    const [questions, setQuestions] = useState([]);
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [previewItem, setPreviewItem] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
    const [downloadingTemplate, setDownloadingTemplate] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    // 'category' = the nested Questionnaire -> Category accordion, and the
    // default: the bank runs to several hundred questions, so opening on a
    // flat paginated list meant page 1 of ~40 with no sense of the shape of
    // it. The accordion leads with the structure and lets you open only the
    // part you want.
    // 'table' = the original flat, paginated list, one click away — still the
    // more scannable view for auditing everything at once. Both read from the
    // same filteredQuestions; nothing about the underlying data differs, only
    // how it's presented.
    const [viewMode, setViewMode] = useState('category');
    const [currentPage, setCurrentPage] = useState(0);
    // Questionnaire/category groups a user has manually expanded, in Category
    // View. Category keys are `${questionnaire}::${category}` since the same
    // category name can exist under more than one questionnaire and their
    // expand state shouldn't be linked. While a search term is active, every
    // group with a match renders open regardless of these sets (see isOpen
    // below) — otherwise a collapsed group could hide the very result
    // someone just searched for.
    const [expandedQuestionnaires, setExpandedQuestionnaires] = useState(() => new Set());
    const [expandedCategories, setExpandedCategories] = useState(() => new Set());
    const { confirm, confirmModal } = useConfirm();

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3500);
    };

    const loadQuestions = useCallback(async () => {
        setLoadingQuestions(true);
        try {
            const response = await thirdpartyExternalExposureQuestionsService('');
            const data = response?.statusCode?.response_data?.data || {};
            const normalized = [
                ...safeArray(data?.admin_questions).map((item) => ({ ...item, source: 'Admin' })),
                ...safeArray(data?.organization_questions).map((item) => ({ ...item, source: 'Organization' })),
            ];
            setQuestions(normalized);
        } catch (error) {
            console.error('Failed to load TPCRM questions:', error);
            setQuestions([]);
        } finally {
            setLoadingQuestions(false);
        }
    }, []);

    useEffect(() => {
        loadQuestions();
    }, [loadQuestions]);

    const handleEditClick = (question) => {
        setEditingQuestion(question);
        setShowCreateModal(true);
    };

    const handleDeleteClick = async (question) => {
        if (!(await confirm({
            title: 'Delete question',
            message: `Delete "${question?.question_text || 'this question'}"? This cannot be undone.`,
            confirmLabel: 'Delete',
            danger: true,
            themeColor: false,
        }))) return;
        try {
            const response = await deleteThirdpartyQuestionsService(`${question.id}/`);
            const message = response?.statusCode?.response_data?.message || response?.statusCode?.message;
            showToast(message || 'Question deleted.', 'success');
            loadQuestions();
        } catch (error) {
            console.error('Failed to delete question:', error);
            showToast('Failed to delete question.', 'error');
        }
    };

    const handleDownloadTemplate = async () => {
        setDownloadingTemplate(true);
        try {
            const response = await getThirdpartyQuestionsTemplateService();
            const data = response?.statusCode;
            if (!data) throw new Error('Empty response');
            const fileBlob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(fileBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'organization_questions_template.xlsx';
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 100);
        } catch (error) {
            console.error('Template download failed:', error);
            showToast('Unable to download the template.', 'error');
        } finally {
            setDownloadingTemplate(false);
        }
    };

    // Distinct questionnaire names across the whole bank (not just the
    // filtered set), so the filter's own option list stays stable no matter
    // what's currently selected.
    const questionnaireFilterOptions = useMemo(() => {
        const names = new Set(questions.map((question) => question?.questionnaire_name || 'Default'));
        return [
            { value: '', label: 'All Questionnaires' },
            ...Array.from(names).sort().map((name) => ({ value: name, label: name })),
        ];
    }, [questions]);

    const filteredQuestions = useMemo(() => {
        const term = search.trim().toLowerCase();
        return questions.filter((question) => {
            const questionnaireName = question?.questionnaire_name || 'Default';
            if (questionnaireFilter && questionnaireName !== questionnaireFilter) return false;
            if (!term) return true;
            const haystack = [
                question?.question_text,
                question?.questionnaire_name,
                question?.category_display,
                question?.category,
                question?.response_type_display,
                question?.response_type,
                question?.source,
            ].filter(Boolean).join(' ').toLowerCase();
            return haystack.includes(term);
        });
    }, [questions, search, questionnaireFilter]);

    useEffect(() => {
        setCurrentPage(0);
    }, [search, questionnaireFilter, viewMode]);

    const hasQuestionFilters = Boolean(search.trim() || questionnaireFilter);
    const questionPageCount = Math.max(1, Math.ceil(filteredQuestions.length / PER_PAGE));
    const pagedQuestions = filteredQuestions.slice(currentPage * PER_PAGE, currentPage * PER_PAGE + PER_PAGE);
    const handlePageClick = ({ selected }) => setCurrentPage(selected);

    // Nested Questionnaire -> Category grouping — two question banks with a
    // same-named category (e.g. both have "AI / ML Service Providers")
    // render as two separate sections instead of silently merging into one,
    // which was the actual problem with the old single-level category view.
    const groupedByQuestionnaire = useMemo(() => {
        const questionnaireGroups = new Map();
        filteredQuestions.forEach((question) => {
            const questionnaireName = question?.questionnaire_name || 'Default';
            if (!questionnaireGroups.has(questionnaireName)) questionnaireGroups.set(questionnaireName, new Map());
            const categoryGroups = questionnaireGroups.get(questionnaireName);
            // Suffix admin-seeded questions' category key so they never
            // silently merge into an org-authored group sharing the same
            // category name — restores OrganizationQuestions.js's original
            // `${categoryName} (${createdBy})` disambiguation, keyed off
            // this page's own `source` field instead of `created_by_que`.
            const categoryBase = question?.category_display || question?.category || 'Uncategorized';
            const categoryKey = question?.source === 'Admin' ? `${categoryBase} (Admin)` : categoryBase;
            if (!categoryGroups.has(categoryKey)) categoryGroups.set(categoryKey, []);
            categoryGroups.get(categoryKey).push(question);
        });

        return Array.from(questionnaireGroups.entries())
            .map(([questionnaire, categoryGroups]) => {
                const categories = Array.from(categoryGroups.entries())
                    .map(([category, items]) => ({ category, items }))
                    .sort((a, b) => a.category.localeCompare(b.category));
                const total = categories.reduce((sum, { items }) => sum + items.length, 0);
                return { questionnaire, categories, total };
            })
            .sort((a, b) => a.questionnaire.localeCompare(b.questionnaire));
    }, [filteredQuestions]);

    // Every expandable key currently on screen, in the same shape the two
    // expanded-* Sets use. Derived from the FILTERED groups, so Expand All
    // opens what you can actually see rather than the whole bank.
    const expandableKeys = useMemo(() => {
        const questionnaires = [];
        const categories = [];
        groupedByQuestionnaire.forEach(({ questionnaire, categories: cats }) => {
            questionnaires.push(questionnaire);
            cats.forEach(({ category }) => categories.push(`${questionnaire}::${category}`));
        });
        return { questionnaires, categories };
    }, [groupedByQuestionnaire]);

    // Checked key-by-key rather than by comparing Set sizes: the Sets can still
    // hold keys from a previous filter, so a size comparison would report
    // "everything is open" when the visible groups are not.
    const allExpanded = expandableKeys.questionnaires.every((k) => expandedQuestionnaires.has(k))
        && expandableKeys.categories.every((k) => expandedCategories.has(k));
    // While a search is active every matching group renders open regardless of
    // these Sets (so a collapsed group can't hide a hit), which would make
    // both buttons look broken — nothing on screen would change. Disabled with
    // an explanation instead.
    const searchActive = Boolean(search.trim());

    const expandAll = () => {
        setExpandedQuestionnaires(new Set(expandableKeys.questionnaires));
        setExpandedCategories(new Set(expandableKeys.categories));
    };

    const collapseAll = () => {
        setExpandedQuestionnaires(new Set());
        setExpandedCategories(new Set());
    };

    const toggleQuestionnaire = (questionnaire) => {
        setExpandedQuestionnaires((prev) => {
            const next = new Set(prev);
            if (next.has(questionnaire)) next.delete(questionnaire);
            else next.add(questionnaire);
            return next;
        });
    };

    const toggleCategory = (categoryKey) => {
        setExpandedCategories((prev) => {
            const next = new Set(prev);
            if (next.has(categoryKey)) next.delete(categoryKey);
            else next.add(categoryKey);
            return next;
        });
    };

    const buildQuestionRows = (items, { includeQuestionnaire = false, includeCategory = false } = {}) => items.map((question) => ({
        data: [
            <div className="tpcrm-question-copy">
                <div className="tpcrm-question-text">
                    {question?.question_text || <span className="tpcrm-cell-empty">{MISSING_TEXT.question}</span>}
                </div>
            </div>,
            ...(includeQuestionnaire ? [question?.questionnaire_name || 'Default'] : []),
            ...(includeCategory ? [question?.category_display || question?.category
                || <span className="tpcrm-cell-empty">{MISSING_TEXT.category}</span>] : []),
            question?.response_type_display || question?.response_type
                || <span className="tpcrm-cell-empty">{MISSING_TEXT.responseType}</span>,
            question?.source || <span className="tpcrm-cell-empty">{MISSING_TEXT.source}</span>,
            <div className="tpcrm-row-actions">
                <CommonButton
                    text="Preview"
                    className="tpcrm-row-btn"
                    onClick={() => {
                        setPreviewItem(question);
                        setShowPreview(true);
                    }}
                />
                {question?.source === 'Organization' && (
                    <>
                        <CommonButton text="Edit" variant="edit" className="tpcrm-row-btn" onClick={() => handleEditClick(question)} />
                        <CommonButton text="Delete" variant="error" className="tpcrm-row-btn" onClick={() => handleDeleteClick(question)} />
                    </>
                )}
            </div>,
        ],
    }));

    return (
        <div className="tpcrm-page">
            <div className="tpcrm-card cmn_elevated_card">
                <div className="tpcrm-card-header">
                    <div>
                        <h3>Question Bank</h3>
                        <div className="tpcrm-card-subtitle">
                            {loadingQuestions
                                ? 'Loading question bank'
                                : viewMode === 'category'
                                    ? `${filteredQuestions.length} questions across ${groupedByQuestionnaire.length} questionnaires`
                                    : `${filteredQuestions.length} questions found`}
                        </div>
                    </div>

                    <div className="tpcrm-page-actions">
                        <CommonButton
                            text={(
                                <span className="tpcrm-btn-icon-label">
                                    <ModuleIcon name="install" size={15} color="currentColor" />
                                    {downloadingTemplate ? 'Downloading...' : 'Template'}
                                </span>
                            )}
                            className="tpcrm-action-btn"
                            onClick={handleDownloadTemplate}
                            disable={downloadingTemplate}
                        />
                        <CommonButton
                            text={(
                                <span className="tpcrm-btn-icon-label">
                                    <ModuleIcon name="upload" size={15} color="currentColor" />
                                    Bulk Upload
                                </span>
                            )}
                            className="tpcrm-action-btn"
                            onClick={() => setShowBulkUploadModal(true)}
                        />
                        <CommonButton
                            text="+ Create Question"
                            className="tpcrm-action-btn"
                            onClick={() => { setEditingQuestion(null); setShowCreateModal(true); }}
                        />
                    </div>
                </div>

                <div className="tpcrm-page-actions tpcrm-filters-row">
                    <CommonSearchBar
                        placeholder="Search questions"
                        value={search}
                        onChange={setSearch}
                        minWidth={200}
                        style={{ width: 220, maxWidth: 220, flex: '0 0 auto' }}
                    />
                    <CommonDropdown
                        ariaLabel="Questionnaire"
                        value={questionnaireFilter}
                        onChange={setQuestionnaireFilter}
                        options={questionnaireFilterOptions}
                        minWidth={180}
                    />
                    {viewMode === 'category' && groupedByQuestionnaire.length > 0 && (
                        // Title on the wrapper rather than the button: a disabled
                        // button swallows hover events in Chrome, so its own
                        // `title` would never show — and disabled is exactly when
                        // the explanation is needed. Same workaround
                        // VendorAssessmentView uses for its disabled status button.
                        <span
                            className="tpcrm-expand-toggle"
                            title={searchActive
                                ? 'Every matching group opens automatically while a search is active.'
                                : undefined}
                        >
                            <CommonButton
                                type="button"
                                text={allExpanded ? 'Collapse all' : 'Expand all'}
                                className="tpcrm-action-btn"
                                onClick={allExpanded ? collapseAll : expandAll}
                                disable={searchActive}
                            />
                        </span>
                    )}

                    <CommonTabs
                        className="tpcrm-view-toggle"
                        tabs={['Category View', 'Table View']}
                        values={['category', 'table']}
                        active={viewMode}
                        handlers={[() => setViewMode('category'), () => setViewMode('table')]}
                    />
                </div>

                {loadingQuestions ? (
                    <div className="tpcrm-profile-empty">
                        <ButtonLoader />
                        <div className="tpcrm-profile-empty-title">Loading question bank...</div>
                    </div>
                ) : filteredQuestions.length === 0 ? (
                    <div className="tpcrm-profile-empty">
                        <div className="tpcrm-profile-empty-title">No questions found</div>
                        <p className="tpcrm-profile-empty-hint">
                            {hasQuestionFilters
                                ? 'No question matches the current search or questionnaire filter.'
                                : 'The question bank is empty. Create a question or bulk-upload a set to get started.'}
                        </p>
                        {hasQuestionFilters && (
                            <CommonButton text="Clear filters" className="tpcrm-action-btn" onClick={() => { setSearch(''); setQuestionnaireFilter(''); }} />
                        )}
                    </div>
                ) : viewMode === 'table' ? (
                    <>
                        <CommonTable
                            headers={TABLE_HEADERS}
                            rows={buildQuestionRows(pagedQuestions, { includeQuestionnaire: true, includeCategory: true })}
                            noPadding
                            noScroll
                        />
                        {questionPageCount > 1 && (
                            <CommonPagination handlePageClick={handlePageClick} pageCount={questionPageCount} forcePage={currentPage} />
                        )}
                    </>
                ) : (
                    <div className="tpcrm-category-list">
                        {groupedByQuestionnaire.map(({ questionnaire, categories, total }) => {
                            const isQuestionnaireOpen = Boolean(search.trim()) || expandedQuestionnaires.has(questionnaire);
                            return (
                                <div key={questionnaire} className="tpcrm-category-group">
                                    <button
                                        type="button"
                                        className="tpcrm-category-header"
                                        onClick={() => toggleQuestionnaire(questionnaire)}
                                        aria-expanded={isQuestionnaireOpen}
                                    >
                                        <span className="tpcrm-category-title">{questionnaire}</span>
                                        <span className="tpcrm-category-meta">
                                            <span className="tpcrm-category-count">{total} question{total === 1 ? '' : 's'}</span>
                                            <span className={`tpcrm-category-chevron${isQuestionnaireOpen ? ' open' : ''}`}>▾</span>
                                        </span>
                                    </button>
                                    {isQuestionnaireOpen && (
                                        <div className="tpcrm-questionnaire-body">
                                            {categories.map(({ category, items }) => {
                                                const categoryKey = `${questionnaire}::${category}`;
                                                const isCategoryOpen = Boolean(search.trim()) || expandedCategories.has(categoryKey);
                                                return (
                                                    <div key={categoryKey} className="tpcrm-subcategory-group">
                                                        <button
                                                            type="button"
                                                            className="tpcrm-subcategory-header"
                                                            onClick={() => toggleCategory(categoryKey)}
                                                            aria-expanded={isCategoryOpen}
                                                        >
                                                            <span className="tpcrm-subcategory-title">{category}</span>
                                                            <span className="tpcrm-category-meta">
                                                                <span className="tpcrm-category-count">{items.length} question{items.length === 1 ? '' : 's'}</span>
                                                                <span className={`tpcrm-category-chevron${isCategoryOpen ? ' open' : ''}`}>▾</span>
                                                            </span>
                                                        </button>
                                                        {isCategoryOpen && (
                                                            <div className="tpcrm-category-body">
                                                                <CommonTable headers={NESTED_HEADERS} rows={buildQuestionRows(items)} noPadding noScroll />
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <CommonModal show={showPreview} onHide={() => setShowPreview(false)} title="Question Preview" size="lg">
                <div className="tpcrm-preview-card">
                    <div>
                        <div className="tpcrm-profile-label">Question</div>
                        <h4 className="tpcrm-preview-question">{previewItem?.question_text || MISSING_TEXT.question}</h4>
                    </div>

                    <div>
                        <div className="tpcrm-profile-label">Expected Evidence</div>
                        <p className="tpcrm-preview-text">{previewItem?.expected_evidence || 'No evidence guidance provided.'}</p>
                    </div>

                    {previewItem?.explanation && (
                        <div>
                            <div className="tpcrm-profile-label">Explanation</div>
                            <p className="tpcrm-preview-text">{previewItem.explanation}</p>
                        </div>
                    )}

                    {Array.isArray(previewItem?.multi_select_options) && previewItem.multi_select_options.length > 0 && (
                        <div>
                            <div className="tpcrm-profile-label">Options</div>
                            <div className="tpcrm-preview-options">
                                {previewItem.multi_select_options.map((option, index) => (
                                    <span key={index} className="tpcrm-preview-pill">{option}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="tpcrm-preview-meta">
                        <div className="tpcrm-preview-meta-item">
                            <div className="tpcrm-profile-label">Questionnaire</div>
                            <span className="tpcrm-preview-pill">{previewItem?.questionnaire_name || 'Default'}</span>
                        </div>
                        <div className="tpcrm-preview-meta-item">
                            <div className="tpcrm-profile-label">Category</div>
                            <span className="tpcrm-preview-pill">{previewItem?.category_display || previewItem?.category || MISSING_TEXT.category}</span>
                        </div>
                        <div className="tpcrm-preview-meta-item">
                            <div className="tpcrm-profile-label">Response Type</div>
                            <span className="tpcrm-preview-pill">{previewItem?.response_type_display || previewItem?.response_type || MISSING_TEXT.responseType}</span>
                        </div>
                        <div className="tpcrm-preview-meta-item">
                            <div className="tpcrm-profile-label">Source</div>
                            <span className="tpcrm-preview-pill">{previewItem?.source || MISSING_TEXT.source}</span>
                        </div>
                    </div>
                </div>
            </CommonModal>

            {showCreateModal && (
                <CreateTPRMQuestionModal
                    show={showCreateModal}
                    editingQuestion={editingQuestion}
                    onHide={() => { setShowCreateModal(false); setEditingQuestion(null); }}
                    onSuccess={() => {
                        setShowCreateModal(false);
                        setEditingQuestion(null);
                        loadQuestions();
                        showToast(editingQuestion ? 'Question updated.' : 'Question created.', 'success');
                    }}
                />
            )}

            {showBulkUploadModal && (
                <BulkUploadQuestionsModal
                    show={showBulkUploadModal}
                    onHide={() => setShowBulkUploadModal(false)}
                    onSuccess={() => {
                        setShowBulkUploadModal(false);
                        loadQuestions();
                        showToast('Questions uploaded.', 'success');
                    }}
                />
            )}

            {confirmModal}

            {toast.show && (
                <div className={`custom-toast ${toast.type}`}>
                    <span className="message">{toast.message}</span>
                </div>
            )}
        </div>
    );
}

export default TpcrmQuestionnairesView;
